const PANEL_SELECTOR = '[data-nurse-performance-analytics-endpoint]'
const SURFACE_SELECTOR = '[data-nurse-performance-surface]'
const INITIALIZED_ATTRIBUTE = 'data-nurse-performance-analytics-initialized'

export const MINIMUM_CARD_VIEW_MS = 1000
export const MINIMUM_CARD_ENGAGEMENT_MS = 2000
export const MAXIMUM_CARD_ENGAGEMENT_MS = 120000
export const CARD_ENGAGEMENT_ROUNDING_MS = 250
export const MAXIMUM_INTERACTIONS_PER_BATCH = 25
export const INTERACTION_BATCH_FLUSH_MS = 15000

const interactionBatchers = new WeakMap()

const CARD_SURFACES = new Set([
    'daily_coach',
    'monthly_completion',
    'nurse_tier',
    'historic_performance',
    'team_progress',
    'team_benchmark',
])

export function normalizeCardEngagementDuration(durationMs) {
    if (!Number.isFinite(durationMs) || durationMs < MINIMUM_CARD_ENGAGEMENT_MS) {
        return null
    }

    return Math.min(
        MAXIMUM_CARD_ENGAGEMENT_MS,
        Math.round(durationMs / CARD_ENGAGEMENT_ROUNDING_MS) * CARD_ENGAGEMENT_ROUNDING_MS,
    )
}

export function createInteractionId(cryptoObject = globalThis.crypto) {
    if (typeof cryptoObject?.randomUUID === 'function') {
        return cryptoObject.randomUUID()
    }

    const bytes = new Uint8Array(16)

    if (typeof cryptoObject?.getRandomValues === 'function') {
        cryptoObject.getRandomValues(bytes)
    } else {
        bytes.forEach((_, index) => {
            bytes[index] = Math.floor(Math.random() * 256)
        })
    }

    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80

    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export function buildInteractionPayload(interaction, surface, durationMs = 0, cryptoObject = globalThis.crypto) {
    const payload = {
        interaction_id: createInteractionId(cryptoObject),
        interaction,
        surface,
    }

    if ('engaged' === interaction) {
        payload.duration_ms = durationMs
    }

    return payload
}

export function buildInteractionBatchPayload(interactions, cryptoObject = globalThis.crypto) {
    return {
        batch_id: createInteractionId(cryptoObject),
        interactions,
    }
}

export function sendInteractionBatch(
    panel,
    interactions,
    fetchImplementation = globalThis.fetch,
    cryptoObject = globalThis.crypto,
) {
    const endpoint = panel?.dataset?.nursePerformanceAnalyticsEndpoint
    const csrfToken = globalThis.document?.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    if (!endpoint || !csrfToken || !interactions.length || typeof fetchImplementation !== 'function') {
        return false
    }

    fetchImplementation(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify(buildInteractionBatchPayload(interactions, cryptoObject)),
    }).catch(() => {})

    return true
}

export function createInteractionBatcher(
    send,
    schedule = globalThis.setTimeout,
    cancel = globalThis.clearTimeout,
) {
    let pendingInteractions = []
    let scheduledFlush = null

    const flush = () => {
        if (null !== scheduledFlush) {
            cancel(scheduledFlush)
            scheduledFlush = null
        }

        if (0 === pendingInteractions.length) {
            return false
        }

        const interactions = pendingInteractions
        pendingInteractions = []
        send(interactions)

        return true
    }

    const enqueue = (interaction) => {
        pendingInteractions.push(interaction)

        if (pendingInteractions.length >= MAXIMUM_INTERACTIONS_PER_BATCH) {
            flush()
        } else if (null === scheduledFlush) {
            scheduledFlush = schedule(flush, INTERACTION_BATCH_FLUSH_MS)
        }

        return true
    }

    return {
        enqueue,
        flush,
        pendingCount: () => pendingInteractions.length,
    }
}

function interactionBatcherFor(panel, fetchImplementation = globalThis.fetch) {
    if (!interactionBatchers.has(panel)) {
        interactionBatchers.set(panel, createInteractionBatcher(
            (interactions) => sendInteractionBatch(panel, interactions, fetchImplementation),
        ))
    }

    return interactionBatchers.get(panel)
}

export function recordNursePerformanceInteraction(
    panel,
    interaction,
    surface,
    durationMs = 0,
    fetchImplementation = globalThis.fetch,
) {
    const endpoint = panel?.dataset?.nursePerformanceAnalyticsEndpoint
    const csrfToken = globalThis.document?.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    if (!endpoint || !csrfToken || typeof fetchImplementation !== 'function') {
        return false
    }

    interactionBatcherFor(panel, fetchImplementation).enqueue(
        buildInteractionPayload(interaction, surface, durationMs),
    )

    return true
}

function canMeasureAttention() {
    return document.visibilityState === 'visible' && document.hasFocus()
}

function initializeVisibilityTracking(panel, surfaces) {
    if (typeof IntersectionObserver !== 'function') {
        return
    }

    const visible = new Map()
    const timers = new Map()
    const recorded = new WeakSet()

    const clearTimer = (element) => {
        const timer = timers.get(element)

        if (timer) {
            window.clearTimeout(timer)
            timers.delete(element)
        }
    }

    const schedule = (element) => {
        if (recorded.has(element) || timers.has(element) || !visible.get(element) || !canMeasureAttention() || panel.hasAttribute('data-monthly-postcard-active')) {
            return
        }

        timers.set(element, window.setTimeout(() => {
            timers.delete(element)

            if (recorded.has(element) || !visible.get(element) || !canMeasureAttention() || panel.hasAttribute('data-monthly-postcard-active')) {
                return
            }

            recorded.add(element)
            recordNursePerformanceInteraction(panel, 'viewed', element.dataset.nursePerformanceSurface)
            observer.unobserve(element)
        }, MINIMUM_CARD_VIEW_MS))
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            visible.set(entry.target, entry.isIntersecting && entry.intersectionRatio >= 0.6)

            if (visible.get(entry.target)) {
                schedule(entry.target)
            } else {
                clearTimer(entry.target)
            }
        })
    }, {threshold: [0.6]})

    surfaces.forEach((surface) => observer.observe(surface))

    const pause = () => surfaces.forEach(clearTimer)
    const resume = () => surfaces.forEach(schedule)

    document.addEventListener('visibilitychange', () => {
        document.visibilityState === 'visible' ? resume() : pause()
    })
    window.addEventListener('focus', resume)
    window.addEventListener('blur', pause)
    panel.addEventListener('monthly-postcard:closed', resume)
}

function initializeEngagementTracking(panel, cards) {
    const states = new Map()

    cards.forEach((card) => {
        const state = {
            pointer: false,
            focus: false,
            startedAt: null,
            elapsedMs: 0,
            recorded: false,
        }

        states.set(card, state)

        const synchronize = () => {
            const shouldRun = !state.recorded && (state.pointer || state.focus) && canMeasureAttention()

            if (shouldRun && null === state.startedAt) {
                state.startedAt = performance.now()
            }

            if (!shouldRun && null !== state.startedAt) {
                state.elapsedMs += performance.now() - state.startedAt
                state.startedAt = null
            }
        }

        const finish = () => {
            synchronize()

            const durationMs = normalizeCardEngagementDuration(state.elapsedMs)

            if (null === durationMs || state.recorded) {
                return
            }

            state.recorded = true
            recordNursePerformanceInteraction(
                panel,
                'engaged',
                card.dataset.nursePerformanceSurface,
                durationMs,
            )
        }

        card.addEventListener('pointerenter', () => {
            state.pointer = true
            synchronize()
        })
        card.addEventListener('pointerleave', () => {
            state.pointer = false
            synchronize()

            if (!state.focus) {
                finish()
            }
        })
        card.addEventListener('focusin', () => {
            state.focus = true
            synchronize()
        })
        card.addEventListener('focusout', (event) => {
            if (event.relatedTarget instanceof Node && card.contains(event.relatedTarget)) {
                return
            }

            state.focus = false
            synchronize()

            if (!state.pointer) {
                finish()
            }
        })

        state.synchronize = synchronize
        state.finish = finish
    })

    const pauseAndRecord = () => states.forEach((state) => {
        state.synchronize()
        state.finish()
    })
    const resume = () => states.forEach((state) => state.synchronize())

    document.addEventListener('visibilitychange', () => {
        document.visibilityState === 'visible' ? resume() : pauseAndRecord()
    })
    window.addEventListener('focus', resume)
    window.addEventListener('blur', pauseAndRecord)
    window.addEventListener('pagehide', pauseAndRecord)
}

function initializePanel(panel) {
    if (panel.hasAttribute(INITIALIZED_ATTRIBUTE) || !panel.dataset.nursePerformanceAnalyticsEndpoint) {
        return
    }

    panel.setAttribute(INITIALIZED_ATTRIBUTE, 'true')

    const surfaces = Array.from(panel.querySelectorAll(SURFACE_SELECTOR))
    surfaces.unshift(panel)

    const cards = surfaces.filter((surface) => CARD_SURFACES.has(surface.dataset.nursePerformanceSurface))

    initializeVisibilityTracking(panel, surfaces)
    initializeEngagementTracking(panel, cards)

    const batcher = interactionBatcherFor(panel)

    document.addEventListener('visibilitychange', () => {
        if ('hidden' === document.visibilityState) {
            batcher.flush()
        }
    })
    window.addEventListener('pagehide', () => batcher.flush())

    panel.addEventListener('click', (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[data-cy="nurse-performance-toggle"]')) {
            return
        }

        queueMicrotask(() => {
            const disclosure = panel.querySelector('[data-nurse-performance-disclosure]')

            if (disclosure instanceof HTMLInputElement) {
                recordNursePerformanceInteraction(
                    panel,
                    disclosure.checked ? 'expanded' : 'collapsed',
                    'panel',
                )
            }
        })
    })
}

function initializePanels(root = document) {
    if (root.matches?.(PANEL_SELECTOR)) {
        initializePanel(root)
    }

    root.querySelectorAll?.(PANEL_SELECTOR).forEach(initializePanel)
}

if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => initializePanels())
    document.addEventListener('livewire:navigated', () => initializePanels())

    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                if (node instanceof HTMLElement) {
                    initializePanels(node)
                }
            })
        })
    }).observe(document.documentElement, {childList: true, subtree: true})
}
